// MP-OS-188 round 3, bug #1 — what the two Sales Trends charts plot, and what they are called.
//
// (a) In a supplier scope, comparing competitor brands (ZYN + VELO) with no own brand selected
//     used to drop the supplier from both charts entirely: Björkkrans saw ZYN and VELO, never
//     CAMO. The supplier's own total now rides along as ONE reference series, emphasised as
//     own, outside the competitor cap. When the user selects an own brand explicitly, that
//     brand IS the reference, so no extra total is added (no double counting). The admin
//     whole-market profile has no own brand, so it never gets one.
// (b) The share chart was titled "<Supplier> · market share over time" while its lines were
//     ZYN and VELO. Both titles are now built from the series actually plotted.
//
// Pure so the title/legend/series agreement is tested over every scope × selection combination.
import { isOwn } from './brandSelection'

export const ownReferenceLabel = (scopeLabel: string): string => `${scopeLabel} (own brands)`

export interface TrendSeriesPlan {
  /** The supplier-total reference series' name, or null when none is plotted. */
  reference: string | null
  /** Every series name in plot (and legend) order — the reference first, as the own emphasis. */
  series: string[]
  /** Brands to emphasise as own in the charts: the resolved own brands plus the reference. */
  emphasis: string[]
}

export function trendSeriesPlan(opts: {
  /** The active supplier scope; empty for the admin whole-market profile. */
  manufacturer: string
  scopeLabel: string
  perBrand: boolean
  /** The selected brands in plot order (orderedSelection). */
  plotBrands: readonly string[]
  /** Own brands from the registry AND the server (BrandOption.own). */
  ownBrands: readonly string[]
}): TrendSeriesPlan {
  const { manufacturer, scopeLabel, perBrand, plotBrands, ownBrands } = opts
  if (!perBrand || plotBrands.length === 0) {
    // The aggregate: a single series, the scope's own total (or the whole market).
    return { reference: null, series: [scopeLabel], emphasis: [...ownBrands] }
  }
  const ownSelected = plotBrands.some((b) => isOwn(b, ownBrands))
  const reference = manufacturer && !ownSelected ? ownReferenceLabel(scopeLabel) : null
  return {
    reference,
    series: reference ? [reference, ...plotBrands] : [...plotBrands],
    emphasis: reference ? [...ownBrands, reference] : [...ownBrands],
  }
}

/** Both chart titles, named after the plotted series rather than the scope. */
export function trendChartTitles(plan: TrendSeriesPlan, opts: {
  perBrand: boolean
  wholeMarket: boolean
  scopeLabel: string
  /** " · <segment>" or "" */
  segmentSuffix: string
}): { cu: string; share: string } {
  const { perBrand, wholeMarket, scopeLabel, segmentSuffix } = opts
  if (!perBrand) {
    return {
      cu: `${scopeLabel} · CU sold over time${segmentSuffix}`,
      share: wholeMarket ? 'Market share · choose a brand or supplier' : `${scopeLabel} · market share over time${segmentSuffix}`,
    }
  }
  const names = plan.series.join(' · ') || 'Select brands'
  return {
    cu: `${names} · CU sold over time${segmentSuffix}`,
    share: `${names} · market share over time${segmentSuffix}`,
  }
}
